// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — Hoja de Ruta PEMM (Tanda B · 0741)
// Renderiza el roadmap que produce el análisis IA (metodología del máster MDPE):
// matriz PICK (impacto × complejidad, SVG) + iniciativas por fase (Fundamentos /
// Consolidación / Optimización) con owner/KPI/brecha/Kotter + DAFO cruces FO/FA/DO/DA.
// Reutiliza el kit visual del BPMM (components/analisis-ui-proceso).
// ════════════════════════════════════════════════════════════════════════════
import { useI18n } from '../../../../i18n';
import { COLORS, SectionCard, Badge } from './components/analisis-ui-proceso';
import type { RoadmapPemm, PickPemm, FaseRoadmap } from './api-pemm-proceso';

const FASE_ORDEN: FaseRoadmap[] = ['FUNDAMENTOS', 'CONSOLIDACION', 'OPTIMIZACION'];
const FASE_LABEL: Record<FaseRoadmap, string> = {
  FUNDAMENTOS: 'Fundamentos',
  CONSOLIDACION: 'Consolidación',
  OPTIMIZACION: 'Optimización',
};
const PICK_LABEL: Record<PickPemm, string> = {
  IMPLEMENT: 'Implement',
  CHALLENGE: 'Challenge',
  POSSIBLE: 'Possible',
  KILL: 'Kill',
};
const PICK_TONO: Record<PickPemm, 'verde' | 'ambar' | 'azul' | 'rojo'> = {
  IMPLEMENT: 'verde',
  CHALLENGE: 'ambar',
  POSSIBLE: 'azul',
  KILL: 'rojo',
};
const PICK_HEX: Record<PickPemm, string> = {
  IMPLEMENT: COLORS.verde,
  CHALLENGE: COLORS.ambar,
  POSSIBLE: COLORS.a2,
  KILL: COLORS.rojo,
};

// ── Matriz PICK (impacto × complejidad) ──────────────────────────────────────
function MatrizPick({ iniciativas }: { iniciativas: RoadmapPemm['iniciativas'] }) {
  const items = iniciativas ?? [];
  const P = 34;
  const W = 250;
  const H = 200;
  const x = (c: number) => P + ((Math.min(5, Math.max(1, c)) - 1) / 4) * W;
  const y = (i: number) => P + ((5 - Math.min(5, Math.max(1, i))) / 4) * H;
  const xMid = P + W / 2;
  const yMid = P + H / 2;
  // Agrupa por celda para separar puntos solapados.
  const offset = new Map<string, number>();
  return (
    <svg
      viewBox={`0 0 ${P + W + 16} ${P + H + 34}`}
      style={{ width: '100%', maxWidth: 420, fontFamily: "'DM Sans',sans-serif" }}
    >
      {/* Cuadrantes */}
      <rect x={P} y={P} width={W / 2} height={H / 2} fill="#EAF7F0" />
      <rect x={xMid} y={P} width={W / 2} height={H / 2} fill="#FFF7E6" />
      <rect x={P} y={yMid} width={W / 2} height={H / 2} fill="#EAF1FB" />
      <rect x={xMid} y={yMid} width={W / 2} height={H / 2} fill="#FBECEC" />
      {/* Etiquetas de cuadrante */}
      <text x={P + 6} y={P + 14} fontSize="9" fontWeight="800" fill={COLORS.verde}>
        IMPLEMENT
      </text>
      <text x={xMid + 6} y={P + 14} fontSize="9" fontWeight="800" fill="#7A5C00">
        CHALLENGE
      </text>
      <text x={P + 6} y={yMid + 14} fontSize="9" fontWeight="800" fill={COLORS.a2}>
        POSSIBLE
      </text>
      <text x={xMid + 6} y={yMid + 14} fontSize="9" fontWeight="800" fill={COLORS.rojo}>
        KILL
      </text>
      {/* Ejes */}
      <line x1={P} y1={P + H} x2={P + W} y2={P + H} stroke={COLORS.gm} strokeWidth="1" />
      <line x1={P} y1={P} x2={P} y2={P + H} stroke={COLORS.gm} strokeWidth="1" />
      <text x={P + W / 2} y={P + H + 24} fontSize="9" fill={COLORS.go} textAnchor="middle">
        Complejidad →
      </text>
      <text
        x={12}
        y={P + H / 2}
        fontSize="9"
        fill={COLORS.go}
        textAnchor="middle"
        transform={`rotate(-90 12 ${P + H / 2})`}
      >
        Impacto →
      </text>
      {/* Puntos */}
      {items.map((it, idx) => {
        const key = `${Math.round(it.complejidad)}-${Math.round(it.impacto)}`;
        const n = offset.get(key) ?? 0;
        offset.set(key, n + 1);
        const px = x(it.complejidad) + (n % 2 === 0 ? n * 4 : -n * 4);
        const py = y(it.impacto) + (n > 1 ? (n - 1) * 3 : 0);
        return (
          <g key={idx}>
            <circle cx={px} cy={py} r="8" fill={PICK_HEX[it.pick]} opacity="0.85" />
            <text x={px} y={py + 3} fontSize="7" fontWeight="700" fill="#fff" textAnchor="middle">
              {it.id}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function MadurezPemmRoadmap({ roadmap }: { roadmap: RoadmapPemm }) {
  const { t } = useI18n();
  const k = ((t.process as Record<string, unknown>)?.madurezPemm ?? {}) as Record<string, string>;
  const tx = (key: string, fallback: string): string => k[key] ?? fallback;

  const iniciativas = roadmap.iniciativas ?? [];
  if (!iniciativas.length && !roadmap.dafoCruces) return null;

  const faseMeta = new Map((roadmap.fases ?? []).map((f) => [f.clave, f]));
  const cruces = roadmap.dafoCruces;
  const crucesBloques: [string, string, string[]][] = cruces
    ? [
        ['fo', tx('cruceFo', 'FO · Fortalezas + Oportunidades (ofensiva)'), cruces.fo],
        ['do', tx('cruceDo', 'DO · Debilidades + Oportunidades (reorientación)'), cruces.do],
        ['fa', tx('cruceFa', 'FA · Fortalezas + Amenazas (defensiva)'), cruces.fa],
        ['da', tx('cruceDa', 'DA · Debilidades + Amenazas (supervivencia)'), cruces.da],
      ]
    : [];

  return (
    <>
      <SectionCard
        title={tx('roadmapTitulo', 'Hoja de Ruta')}
        accent={COLORS.cyan}
        hint={iniciativas.length ? `${iniciativas.length} iniciativas` : undefined}
      >
        {iniciativas.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            {/* Matriz PICK */}
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: COLORS.a2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  margin: '0 0 4px',
                }}
              >
                {tx('matrizPick', 'Priorización PICK (impacto × complejidad)')}
              </p>
              <MatrizPick iniciativas={iniciativas} />
            </div>
            {/* Iniciativas por fase */}
            <div style={{ flex: '2 1 360px', minWidth: 320 }}>
              {FASE_ORDEN.filter((f) => iniciativas.some((i) => i.fase === f)).map((fase) => {
                const meta = faseMeta.get(fase);
                const ini = iniciativas.filter((i) => i.fase === fase);
                return (
                  <div key={fase} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: COLORS.a1,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        marginBottom: 2,
                      }}
                    >
                      {meta?.nombre || FASE_LABEL[fase]}
                    </div>
                    {meta?.objetivo && (
                      <div style={{ fontSize: 11, color: COLORS.go, marginBottom: 6 }}>
                        {meta.objetivo}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ini.map((it) => (
                        <div
                          key={it.id}
                          style={{
                            border: `1px solid ${COLORS.gm}`,
                            borderLeft: `3px solid ${PICK_HEX[it.pick]}`,
                            borderRadius: 8,
                            padding: '8px 10px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              flexWrap: 'wrap',
                              marginBottom: 3,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: COLORS.w,
                                background: COLORS.a1,
                                borderRadius: 6,
                                padding: '1px 7px',
                              }}
                            >
                              {it.id}
                            </span>
                            <Badge tono={PICK_TONO[it.pick]}>{PICK_LABEL[it.pick]}</Badge>
                            {(it.kotter ?? []).length > 0 && (
                              <span style={{ fontSize: 10, color: COLORS.go }}>
                                {tx('kotter', 'Kotter')}:{' '}
                                {(it.kotter ?? []).map((kk) => `K${kk}`).join(' ')}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.45 }}>
                            {it.titulo}
                          </div>
                          <div
                            style={{
                              fontSize: 10.5,
                              color: COLORS.go,
                              marginTop: 3,
                              lineHeight: 1.4,
                            }}
                          >
                            {it.brecha && (
                              <span>
                                <strong>{tx('brecha', 'Brecha')}:</strong> {it.brecha}{' '}
                              </span>
                            )}
                            {it.owner && (
                              <span>
                                · <strong>{tx('owner', 'Owner')}:</strong> {it.owner}{' '}
                              </span>
                            )}
                            {it.kpi && (
                              <span>
                                · <strong>KPI:</strong> {it.kpi}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>

      {/* DAFO cruzado (estrategias) */}
      {crucesBloques.length > 0 && (
        <SectionCard title={tx('crucesTitulo', 'Estrategias cruzadas (DAFO)')} accent={COLORS.a2}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {crucesBloques.map(([clave, titulo, items]) => (
              <div
                key={clave}
                style={{ border: `1px solid ${COLORS.gm}`, borderRadius: 8, padding: 10 }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.a1, marginBottom: 5 }}>
                  {titulo}
                </div>
                {items.length === 0 ? (
                  <div style={{ fontSize: 11, color: COLORS.go, fontStyle: 'italic' }}>—</div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                    {items.map((it, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 6,
                          fontSize: 12,
                          color: COLORS.ink,
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ color: COLORS.a2, fontWeight: 900, flexShrink: 0 }}>›</span>
                        <span style={{ lineHeight: 1.4 }}>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );
}
